import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import BlockModel from '../models/email-template/block-model.interface';
import { TemporaryMedia } from '../models/email-template/temporary-media.interface';
import { TemplateService } from './template.service';
import { GlobalTemplateService } from './global-template.service';

@Injectable({ providedIn: 'root' })
export class MediaManagementService {

  templateService = inject(TemplateService);
  globalTemplateService = inject(GlobalTemplateService);

  /**
   * Stores a newly picked image for a block and emits the URI to put in the
   * block's variable. Shared by every place an image can be picked (the
   * app-image-upload widget, the v2 canvas), so they all store it the same way.
   *
   * A new template has nowhere to upload to yet: the file is staged as
   * temporary media under a local blob URI, which the save swaps for the
   * uploaded one (see createMediaObservablesArray). A saved template uploads
   * right away, and the image it replaces is queued for deletion on save.
   *
   * @param file - The picked image file.
   * @param blockId - The block the image belongs to.
   * @param previousSrc - The image being replaced, if any.
   */
  storeImage(file: File, blockId: string, previousSrc?: string | null): Observable<string> {
    return combineLatest([
      this.globalTemplateService.isNewTemplateData$,
      this.globalTemplateService.templateNameData$,
    ]).pipe(
      take(1),
      switchMap(([isTemplateNew, templateName]) => {
        if (isTemplateNew) {
          const tempMediaURI = URL.createObjectURL(file);
          this.globalTemplateService.setTemporaryMediaData({ tempMediaURI, file, blockId });
          return of(tempMediaURI);
        }

        return this.templateService.uploadImage(file, templateName, blockId).pipe(
          tap(() => {
            if (previousSrc) {
              this.globalTemplateService.setMediaForDeletionData(previousSrc);
            }
          })
        );
      })
    );
  }


  /**
     * Iterates over the selected blocks and their variables to find temporary media,
     * creates upload observables for each found media file, and pushes them into the provided array.
     * When a media file is uploaded, updates the corresponding variable in the block with the upload result.
     *
     * @param uploadMediaObservables - An array to which the generated media upload observables will be pushed.
     */
  createMediaObservablesArray(
    selectedBlocks: BlockModel[],
    temporaryMedia: TemporaryMedia[],
    templateName: string
  ): Array<Observable<string>> {

    const uploadMediaObservables: Array<Observable<string>> = [];

    // Iterates over the selected blocks and their variables to find temporary media
    selectedBlocks.forEach((block) => {
      Object.entries(block.variables || {}).forEach(([key, value]) => {
        // This matches media by their temporary localhost URI blob
        const foundMedia = temporaryMedia.find(
          (media) => media.tempMediaURI === value
        );

        // Creates an observable for each found media
        if (foundMedia) {
          const upload = foundMedia.kind === 'video'
            ? this.templateService.uploadVideo(foundMedia.file, templateName!, foundMedia.blockId)
            : this.templateService.uploadImage(foundMedia.file, templateName!, foundMedia.blockId);

          const upload$ = upload
            .pipe(
              tap((res) => {
                // Writes back into the actual variables object
                block.variables![key] = res;
                // Set the new media uri back in the array with temporary media
                const indexOfFoundMedia = temporaryMedia.findIndex(
                  (media) => media.tempMediaURI === foundMedia.tempMediaURI
                );
                foundMedia.newMediaURI = res;
                temporaryMedia[indexOfFoundMedia] = foundMedia;
              })
            );

          // Push each observable into a single array which will be used with forkJoin
          uploadMediaObservables.push(upload$);
        }
      });
    });

    return uploadMediaObservables;
  }

  /////////////////////////////////////
  /////////////////////////////////////
  /////////////////////////////////////
  /////////////////////////////////////

   /**
   * Populates the provided array with observables that delete media within each block.
   *
   * Iterates over the `mediaForDeletionWithinABlock` array, and for each block ID,
   * creates an observable by calling `templateService.deleteMediaWithinABlock` with
   * the current template name and block ID. Each observable is then pushed into the
   * `mediaForDeletionWithinABlockObservables` array.
   *
   * @param mediaForDeletionWithinABlockObservables - An array to be populated with observables for deleting media within blocks.
   */
  populateMediaForDeletionWithinABlockObservables(
    mediaForDeletionWithinABlockObservables: Observable<string>[],
    mediaForDeletionWithinABlock: string[],
    templateName: string
  ) {
    mediaForDeletionWithinABlock.forEach((blockId) => {
      const deleteMedia$ = this.templateService.deleteMediaWithinABlock(
        templateName,
        blockId
      );

      mediaForDeletionWithinABlockObservables.push(deleteMedia$);
    });
    return mediaForDeletionWithinABlockObservables;
  }
  /////////////////////////////////////
  /////////////////////////////////////
  /////////////////////////////////////
  /////////////////////////////////////
    /**
     * Populates the provided array with observables for deleting media.
     *
     * Iterates over the `mediaForDeletion` array and, for each media URI,
     * creates an observable by calling `templateService.deleteMedia(mediaURI)`.
     * Each observable is then pushed into the `mediaForDeletionObservables` array.
     *
     * @param mediaForDeletionObservables - The array to be populated with deletion observables.
     */
  populateMediaForDeletionObservables(mediaForDeletionObservables: Observable<string>[],
    mediaForDeletion: string[]
  ) {
    mediaForDeletion.forEach((mediaURI) => {
      const deleteMedia$ = this.templateService.deleteMedia(mediaURI);

      mediaForDeletionObservables.push(deleteMedia$);
    });
    return mediaForDeletionObservables;
  }
  /////////////////////////////////////
  /////////////////////////////////////

   generateMediaDeletionForks(mediaForDeletionWithinABlockObservables: Observable<string>[], mediaForDeletionObservables: Observable<string>[]) {
      const deleteAllMediaWithinABlock$ = mediaForDeletionWithinABlockObservables.length > 0
        ? forkJoin(mediaForDeletionWithinABlockObservables)
        : undefined;
  
      const deleteAllMedia$ = mediaForDeletionObservables.length > 0
        ? forkJoin(mediaForDeletionObservables)
        : undefined;
      return { deleteAllMediaWithinABlock$, deleteAllMedia$ };
    }

}
